/**
 * Iris Copilot — agent-assist for the HUMAN operator. Given a conversation,
 * Iris drafts a reply grounded in the same knowledge base the AI agent uses
 * (returning the sources it leaned on + whether the draft is grounded), which
 * the operator can edit and send. An optional free-form instruction lets the
 * operator steer a regenerate ("shorter", "offer the earliest slot", "ask for
 * the address first").
 *
 * This mirrors Intercom Fin's Copilot. Proxied through operations at
 * `/api/messaging/iris/copilot` (operations is the FE's auth boundary + RBAC
 * gate; it forwards to ai-operations with the shared service key), mirroring
 * the sibling `guidance.ts` / `inspection.ts` / `review.ts` hooks.
 */
import { useMutation } from '@tanstack/react-query';
import { apiPost, operationsClient } from '@/lib/api';

const BASE = '/api/messaging/iris/copilot';

/** A knowledge snippet the draft was grounded in. */
export interface CopilotSource {
  title: string | null;
  text: string;
}

/** The generated draft reply plus the grounding that produced it. */
export interface CopilotDraft {
  /** The suggested reply text the operator can edit + send. */
  draft: string;
  /** Whether the draft was grounded in retrieved knowledge. */
  grounded: boolean;
  /** Knowledge snippets that informed the draft. */
  sources: CopilotSource[];
}

/** Request a Copilot draft for a conversation, optionally steered. */
export interface DraftReplyPayload {
  conversationId: string;
  /** Optional operator steer for a regenerate ("shorter", "be firmer", …). */
  instruction?: string;
}

/**
 * Draft (or regenerate) a grounded reply for a conversation. Not a query —
 * generation is operator-triggered and side-effect-free, so it's modeled as a
 * mutation the panel fires on demand.
 */
export function useDraftReply() {
  return useMutation({
    mutationFn: (payload: DraftReplyPayload) =>
      apiPost<CopilotDraft, DraftReplyPayload>(operationsClient, `${BASE}/draft`, payload),
  });
}
